import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, FileText, Download } from "lucide-react";
import { formatDateDE } from "@/lib/utils";
import { MWST_RATE } from "@/lib/vatConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuftragBestaetigen() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [depositConfirming, setDepositConfirming] = useState(false);
  const [depositConfirmed, setDepositConfirmed] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/inquiries/by-order-token/${token}`);
        if (!mounted) return;
        setData(res.data);
        if (res.data?.order_confirmed_by_customer_at) setAccepted(true);
        if (res.data?.deposit_confirmed_by_customer_at || res.data?.deposit_received_at) setDepositConfirmed(true);
      } catch (err) {
        if (!mounted) return;
        setError("Dieser Bestätigungslink ist ungültig oder abgelaufen.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await axios.post(`${API}/inquiries/by-order-token/${token}/accept`, {});
      setAccepted(true);
      toast.success("Auftrag verbindlich angenommen — vielen Dank!");
    } catch (err) {
      toast.error("Bestätigung fehlgeschlagen. Bitte erneut versuchen.");
    } finally {
      setAccepting(false);
    }
  };

  const handleConfirmDeposit = async () => {
    setDepositConfirming(true);
    try {
      await axios.post(`${API}/inquiries/by-order-token/${token}/confirm-deposit`);
      setDepositConfirmed(true);
      toast.success("Vielen Dank — wir prüfen den Zahlungseingang.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler bei der Meldung. Bitte erneut versuchen.");
    } finally {
      setDepositConfirming(false);
    }
  };

  const formatIban = (iban) => {
    if (!iban) return "";
    return iban.replace(/(.{4})/g, "$1 ").trim();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#BA6D52]" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-red-700">Fehler</p>
          <h1 className="font-serif text-3xl mt-4">{error}</h1>
        </div>
      </div>
    );
  }

  const days = data.confirmed_anzahl_tage || 1;

  return (
    <div data-testid="page-auftrag-bestaetigen" className="min-h-screen bg-[#FAFAF7] py-16 md:py-24">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52]">Lukas Stutz — Mietkoch</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-4">Auftragsbestätigung</h1>
        <p className="text-sm text-[#BA6D52] mt-3 tracking-wider">
          Auftragsnummer: <b>{data.confirmation_number}</b>
        </p>

        {/* CTA am Anfang – damit der Kunde ihn sofort sieht */}
        {!accepted && (
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-emerald-50 border border-emerald-200 p-5 md:p-6">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">Nur ein Klick</p>
              <p className="text-sm text-emerald-900 mt-1 font-light">
                Nach kurzer Prüfung der Details unten hier verbindlich annehmen.
              </p>
            </div>
            <button
              onClick={handleAccept}
              disabled={accepting}
              data-testid="auftrag-accept-btn-top"
              className="w-full sm:w-auto bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 px-6 py-3 text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-3 flex-shrink-0"
            >
              {accepting ? (
                <><Loader2 size={16} className="animate-spin" /> Wird gespeichert …</>
              ) : (
                <><FileText size={16} /> Auftrag verbindlich annehmen</>
              )}
            </button>
          </div>
        )}

        <div className="mt-10 bg-white border border-[#E3DECE] p-6 md:p-8">
          <p className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Einsatzdetails</p>
          <p className="font-serif text-2xl mt-2">{data.betrieb}</p>
          <p className="text-sm text-[#757067] mt-1">{data.ort}</p>

          <table className="w-full mt-6 text-sm">
            <tbody>
              <tr className="border-t border-[#E3DECE]"><td className="py-2 text-[#757067]">Datum</td><td className="py-2 text-right">{formatDateDE(data.einsatzdatum)}</td></tr>
              {data.confirmed_von && data.confirmed_bis && (
                <tr className="border-t border-[#E3DECE]"><td className="py-2 text-[#757067]">Uhrzeit</td><td className="py-2 text-right">{data.confirmed_von} – {data.confirmed_bis} Uhr</td></tr>
              )}
              <tr className="border-t border-[#E3DECE]"><td className="py-2 text-[#757067]">Anzahl Tage</td><td className="py-2 text-right">{days}</td></tr>
            </tbody>
          </table>

          <p className="mt-8 text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Kostenaufstellung</p>
          {(data.confirmed_line_items || []).length > 0 ? (
            <table className="w-full mt-3 text-sm">
              <thead>
                <tr className="bg-[#FAF5F1]">
                  <th className="py-2 px-2 text-left text-[10px] uppercase tracking-[0.15em] text-[#BA6D52]">Position</th>
                  <th className="py-2 px-2 text-right text-[10px] uppercase tracking-[0.15em] text-[#BA6D52]">Menge</th>
                  <th className="py-2 px-2 text-right text-[10px] uppercase tracking-[0.15em] text-[#BA6D52]">Einzelpreis</th>
                  <th className="py-2 px-2 text-right text-[10px] uppercase tracking-[0.15em] text-[#BA6D52]">Summe</th>
                </tr>
              </thead>
              <tbody>
                {(data.confirmed_line_items || []).map((it, idx) => {
                  const qty = Number(it.quantity || 0);
                  const price = Number(it.unit_price_net || 0);
                  const line = Math.round(qty * price * 100) / 100;
                  return (
                    <tr key={idx} className="border-t border-[#E3DECE] align-top">
                      <td className="py-2 px-2">
                        <div className="font-medium text-[#2C2A26]">{it.name}</div>
                        {it.description && <div className="text-xs text-[#757067] mt-0.5">{it.description}</div>}
                      </td>
                      <td className="py-2 px-2 text-right whitespace-nowrap">{qty} {it.unit_name || "Stück"}</td>
                      <td className="py-2 px-2 text-right whitespace-nowrap">{price.toFixed(2).replace(".", ",")} €</td>
                      <td className="py-2 px-2 text-right whitespace-nowrap font-medium">{line.toFixed(2).replace(".", ",")} €</td>
                    </tr>
                  );
                })}
                <tr className="border-t border-[#E3DECE]">
                  <td colSpan={3} className="py-2 px-2 text-right text-[#757067]">Zwischensumme netto</td>
                  <td className="py-2 px-2 text-right font-medium">{Number(data.confirmed_total_eur).toFixed(2).replace(".", ",")} €</td>
                </tr>
                <tr>
                  <td colSpan={3} className="py-2 px-2 text-right text-[#757067]">zzgl. {Math.round((data.mwst_rate || MWST_RATE) * 100)} % MwSt.</td>
                  <td className="py-2 px-2 text-right text-[#757067]">{Number(data.confirmed_mwst_eur || 0).toFixed(2).replace(".", ",")} €</td>
                </tr>
                <tr className="border-t-2 border-[#BA6D52] bg-[#F2EFE9]">
                  <td colSpan={3} className="py-3 px-2 font-medium">Gesamtsumme brutto</td>
                  <td className="py-3 px-2 text-right font-serif text-xl text-[#BA6D52] whitespace-nowrap">
                    {Number(data.confirmed_gross_eur || data.confirmed_total_eur).toFixed(2).replace(".", ",")} €
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <table className="w-full mt-3 text-sm">
              <tbody>
                {(data.weekend_dates || []).length > 0 && data.confirmed_hourly_rate_weekend_eur > 0 ? (
                  <>
                    <tr className="border-t border-[#E3DECE]">
                      <td className="py-2 text-[#757067]">Stundensatz Werktag</td>
                      <td className="py-2 text-right">{Number(data.confirmed_hourly_rate_eur).toFixed(2).replace(".", ",")} € / h netto</td>
                    </tr>
                    <tr className="border-t border-[#E3DECE]">
                      <td className="py-2 text-[#757067]">Stundensatz Wochenende/Feiertag ({data.weekend_dates.length} Tag{data.weekend_dates.length > 1 ? "e" : ""})</td>
                      <td className="py-2 text-right">{Number(data.confirmed_hourly_rate_weekend_eur).toFixed(2).replace(".", ",")} € / h netto</td>
                    </tr>
                  </>
                ) : (
                  <tr className="border-t border-[#E3DECE]">
                    <td className="py-2 text-[#757067]">Stundensatz</td>
                    <td className="py-2 text-right">{Number(data.confirmed_hourly_rate_eur).toFixed(2).replace(".", ",")} € / h netto</td>
                  </tr>
                )}
                <tr className="border-t border-[#E3DECE]">
                  <td className="py-2 text-[#757067]">Lohnkosten (geplant)</td>
                  <td className="py-2 text-right">{Number(data.confirmed_labor_cost_eur).toFixed(2).replace(".", ",")} €</td>
                </tr>
                {Number(data.confirmed_travel_cost_eur || 0) > 0 && (
                <tr className="border-t border-[#E3DECE]">
                  <td className="py-2 text-[#757067]">Anfahrt</td>
                  <td className="py-2 text-right">{Number(data.confirmed_travel_cost_eur).toFixed(2).replace(".", ",")} €</td>
                </tr>
                )}
                <tr className="border-t border-[#E3DECE]">
                  <td className="py-2 text-[#757067]">Zwischensumme netto</td>
                  <td className="py-2 text-right">{Number(data.confirmed_total_eur).toFixed(2).replace(".", ",")} €</td>
                </tr>
                <tr>
                  <td className="py-2 text-[#757067]">zzgl. {Math.round((data.mwst_rate || MWST_RATE) * 100)} % MwSt.</td>
                  <td className="py-2 text-right text-[#757067]">{Number(data.confirmed_mwst_eur || 0).toFixed(2).replace(".", ",")} €</td>
                </tr>
                <tr className="border-t-2 border-[#BA6D52] bg-[#F2EFE9]">
                  <td className="py-3 px-2 font-medium">Gesamtsumme brutto</td>
                  <td className="py-3 px-2 text-right font-serif text-xl text-[#BA6D52]">
                    {Number(data.confirmed_gross_eur || data.confirmed_total_eur).toFixed(2).replace(".", ",")} €
                  </td>
                </tr>
              </tbody>
            </table>
          )}
          <p className="text-xs text-[#757067] italic mt-2">Inkl. ges. MwSt. — endgültige Abrechnung anhand tatsächlich geleisteter Stunden.</p>

          {data.confirmed_notes && (
            <div className="mt-6 bg-[#FAF5F1] border-l-3 border-[#BA6D52] p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Vermerk</p>
              <p className="text-sm text-[#2C2A26] mt-2 font-light whitespace-pre-line">{data.confirmed_notes}</p>
            </div>
          )}
        </div>

        {/* Anzahlung */}
        {data.deposit_percent > 0 && data.deposit_amount_eur > 0 && (
          <div data-testid="deposit-section" className="mt-8 bg-white border-2 border-[#BA6D52] p-6 md:p-8">
            <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Zahlungsbedingungen — Anzahlung</p>
            <table className="w-full mt-4 text-sm">
              <tbody>
                <tr>
                  <td className="py-2 text-[#2C2A26]"><b>Anzahlung ({data.deposit_percent} %) vom Brutto</b></td>
                  <td className="py-2 text-right font-serif text-2xl text-[#BA6D52]">
                    {Number(data.deposit_amount_eur).toFixed(2).replace(".", ",")} €
                  </td>
                </tr>
                {data.deposit_invoice_scheduled_date && (
                  <tr>
                    <td className="py-2 text-[#757067]">Rechnungs-Versand</td>
                    <td className="py-2 text-right">{formatDateDE(data.deposit_invoice_scheduled_date)}</td>
                  </tr>
                )}
                <tr>
                  <td className="py-2 text-[#757067]">Zahlungsfrist</td>
                  <td className="py-2 text-right">{formatDateDE(data.deposit_due_date)}</td>
                </tr>
                <tr className="border-t border-[#E3DECE]">
                  <td className="py-2 text-[#757067]">Restbetrag nach Einsatz (brutto)</td>
                  <td className="py-2 text-right text-[#757067]">
                    {Math.max(0, Number(data.confirmed_gross_eur || data.confirmed_total_eur) - Number(data.deposit_amount_eur)).toFixed(2).replace(".", ",")} €
                  </td>
                </tr>
              </tbody>
            </table>

            {data.deposit_invoice_scheduled_date ? (
              <div className="mt-5 bg-[#FAF5F1] border-l-3 border-[#BA6D52] p-4 text-sm text-[#2C2A26] leading-relaxed">
                <p>
                  Sie erhalten Ihre Anzahlungsrechnung am <b>{formatDateDE(data.deposit_invoice_scheduled_date)}</b> per E-Mail —
                  <b> Kontodaten und Verwendungszweck (Rechnungsnummer)</b> stehen dann auf der Rechnung.
                  Die Zahlungsfrist beträgt <b>{data.deposit_invoice_payment_days || 14} Tage</b> nach Rechnungserhalt
                  (bis spätestens <b>{formatDateDE(data.deposit_due_date)}</b>).
                </p>
                <p className="mt-2 text-xs text-[#757067]">
                  {data.fixed_price
                    ? "Der Restbetrag entspricht dem vereinbarten Festpreis abzüglich der Anzahlung und ist nach dem Einsatz fällig."
                    : "Der Restbetrag wird nach Einsatz auf Basis der tatsächlich geleisteten Stunden fakturiert."}
                </p>
              </div>
            ) : (
              <>
                {data.sender_iban && (
                  <div className="mt-5 bg-[#FAFAF7] border border-[#E3DECE] p-4 text-sm">
                    <p className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Bankverbindung</p>
                    <p className="mt-2 font-mono tracking-wider text-[#2C2A26]"><b>IBAN:</b> {formatIban(data.sender_iban)}</p>
                    {data.sender_bank && <p className="text-[#757067] mt-1"><b>Bank:</b> {data.sender_bank}</p>}
                    <p className="text-[#757067] mt-1"><b>Verwendungszweck:</b> {data.confirmation_number}</p>
                  </div>
                )}

                {depositConfirmed ? (
                  <div className="mt-5 bg-emerald-50 border border-emerald-200 p-4 text-sm">
                    <p className="text-emerald-900">
                      <CheckCircle2 size={16} className="inline mr-2 mb-1" />
                      Ihre Überweisungsmeldung wurde dokumentiert
                      {data.deposit_confirmed_by_customer_at && ` am ${formatDateDE(data.deposit_confirmed_by_customer_at)}`}.
                      Wir prüfen den Zahlungseingang.
                    </p>
                  </div>
                ) : (
                  <div className="mt-5">
                    <p className="text-xs text-[#757067] font-light">
                      Sobald Sie die Anzahlung überwiesen haben, klicken Sie bitte hier — wir prüfen dann den Eingang.
                    </p>
                    <button
                      type="button"
                      onClick={handleConfirmDeposit}
                      disabled={depositConfirming}
                      data-testid="deposit-confirm-btn"
                      className="mt-3 bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-6 py-3 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2"
                    >
                      {depositConfirming ? (
                        <><Loader2 size={14} className="animate-spin" /> Wird gespeichert …</>
                      ) : (
                        <>✓ Anzahlung überwiesen</>
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Action */}
        {accepted ? (
          <div data-testid="auftrag-accepted"
            className="relative mt-8 border border-[#E3DECE] bg-[#FFFDF8] p-8 md:p-12 text-center overflow-hidden animate-in fade-in zoom-in-95 duration-500">
            <span className="absolute top-4 left-4 w-6 h-6 border-t border-l border-[#BA6D52]/60" />
            <span className="absolute top-4 right-4 w-6 h-6 border-t border-r border-[#BA6D52]/60" />
            <span className="absolute bottom-4 left-4 w-6 h-6 border-b border-l border-[#BA6D52]/60" />
            <span className="absolute bottom-4 right-4 w-6 h-6 border-b border-r border-[#BA6D52]/60" />
            <div className="relative mx-auto w-24 h-24 flex items-center justify-center animate-in zoom-in-50 duration-700">
              <span className="absolute inset-0 rounded-full border border-[#BA6D52]/40" />
              <span className="absolute inset-2 rounded-full border border-[#BA6D52]/20" />
              <img src="/logo.png" alt="" className="w-16 h-16 object-contain" />
            </div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#BA6D52] mt-7">Auftrag bestätigt</p>
            <h2 className="font-serif text-3xl font-light mt-4 text-[#2C2A26]">
              Vielen Dank für Ihre Bestätigung
            </h2>
            <p className="text-sm text-[#5A4D43] mt-5 font-light leading-relaxed max-w-md mx-auto">
              Der Termin ist fest eingeplant. Ihre Auftragsbestätigung können Sie hier direkt
              herunterladen — eine Kopie ist auch per E-Mail unterwegs.
            </p>
            <a href={`${API}/inquiries/by-order-token/${token}/pdf`}
              data-testid="auftrag-download"
              className="mt-8 inline-flex items-center justify-center gap-2.5 bg-[#BA6D52] hover:bg-[#9C4F31] text-white px-8 py-4 text-xs uppercase tracking-[0.2em] transition-colors">
              <Download size={15} /> Auftragsbestätigung herunterladen
            </a>
          </div>
        ) : (
          <div className="mt-8 bg-[#FAF5F1] border border-[#BA6D52] p-6 md:p-8">
            <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Verbindliche Annahme</p>
            <p className="text-sm text-[#2C2A26] mt-3 font-light leading-relaxed">
              Mit einem Klick auf den Button nehmen Sie den oben aufgeführten Auftrag zu den
              genannten Konditionen verbindlich an. Es gelten unsere <a href="/agb" target="_blank" rel="noopener noreferrer" className="text-[#BA6D52] underline">AGB</a>.
              Die Annahme wird mit Zeitstempel dokumentiert.
            </p>
            <button
              onClick={handleAccept}
              disabled={accepting}
              data-testid="auftrag-accept-btn"
              className="mt-6 w-full sm:w-auto bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 px-8 py-4 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-3"
            >
              {accepting ? (
                <><Loader2 size={16} className="animate-spin" /> Wird gespeichert …</>
              ) : (
                <><FileText size={16} /> Auftrag verbindlich annehmen</>
              )}
            </button>
          </div>
        )}

        <p className="text-xs text-[#757067] text-center mt-12">
          Lukas Stutz — Mietkoch für Gastronomie &amp; Events
        </p>
      </div>
    </div>
  );
}
